import { createSequence } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import {
  AppGridItem,
  AppGridItemSkeleton,
} from '~/components/molecules/AppGridItem';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { parseOrganizationUserRoles } from '~/helpers/parseOrganizationUserRoles';
import { useOrganizationAppsQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationAppsQuery';
import { useOrganizationUserQuery } from '~/lib/api/modules/Organizations/hooks/useOrganizationUserQuery';

const DEFAULT_SKELETON_COUNT = 12;

const styles = stylex.create({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 120px)',
    gap: 32,
    justifyContent: 'space-between',
  },
});

export type IOrganizationAppGridProps = IContainerProps & {
  organizationId: string;
};

export const OrganizationAppGrid: React.FC<IOrganizationAppGridProps> = (
  props,
) => {
  const { sx, organizationId } = props;
  const { t } = useTranslation();
  const organizationAppsQuery = useOrganizationAppsQuery({ organizationId });
  const organizationUserQuery = useOrganizationUserQuery({ organizationId });
  const organizationUserRoles = organizationUserQuery.data?.roles
    ? parseOrganizationUserRoles(organizationUserQuery.data?.roles)
    : undefined;
  const canCreate = organizationUserRoles?.atLeastAdmin;

  const organizationApps = organizationAppsQuery.data?.items;
  const isEmpty = !organizationAppsQuery.isLoading && !organizationApps?.length;

  if (organizationUserQuery.isError || organizationAppsQuery.isError) {
    return (
      <ContentBlock sx={sx}>
        <ContentBlock.Body first>
          <ErrorMessage
            error={organizationUserQuery.error ?? organizationAppsQuery.error}
          />
        </ContentBlock.Body>
      </ContentBlock>
    );
  }

  if (isEmpty) {
    return (
      <ContentBlock sx={sx}>
        <ContentBlock.Empty title={t('AppList.empty.title')}>
          {canCreate ? (
            <Button
              href={
                organizationId
                  ? `/organizations/${organizationId}/apps/create`
                  : '/apps/create'
              }
            >
              {t('AppList.empty.action-label')}
            </Button>
          ) : undefined}
        </ContentBlock.Empty>
      </ContentBlock>
    );
  }

  return (
    <div {...stylex.props(styles.grid)}>
      {organizationAppsQuery.isLoading
        ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
            <AppGridItemSkeleton key={index} />
          ))
        : organizationApps?.map((app) => (
            <div key={app._id}>
              <AppGridItem app={app} />
            </div>
          ))}
    </div>
  );
};
